<template>
  <div class="bg-white rounded-xl shadow-md p-4 mb-6">
    <div class="flex items-start space-x-3">
      <peer-avatar :user="currentUserAvatar" size="sm" />
      
      <div class="flex-1">
        <textarea
          v-model="postContent"
          placeholder="Share something with the community..."
          class="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
          :rows="isExpanded ? 4 : 2"
          @focus="isExpanded = true"
        ></textarea>
        
        <div v-if="isExpanded" class="mt-3 flex justify-between items-center">
          <div class="flex space-x-2">
            <button 
              @click="selectImage"
              class="flex items-center text-sm text-gray-700 hover:text-blue-600 transition-colors p-1.5 rounded-md hover:bg-blue-50"
            >
              <span class="material-symbols-outlined text-xl mr-1">image</span>
              <span class="hidden sm:inline">Add Photo</span>
            </button>
            <input 
              type="file" 
              ref="fileInput" 
              accept="image/*" 
              class="hidden" 
              @change="handleImageUpload"
            />
          </div>
          
          <div class="flex space-x-2">
            <button 
              @click="cancelPost" 
              class="px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors text-sm"
            >
              Cancel
            </button>
            <button 
              @click="submitPost" 
              :disabled="!canSubmit || loading" 
              class="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="loading" class="inline-flex items-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Posting...
              </span>
              <span v-else>Post</span>
            </button>
          </div>
        </div>
        
        <!-- Image preview -->
        <div v-if="selectedImage" class="mt-3 relative">
          <img 
            :src="selectedImage" 
            alt="Selected image" 
            class="max-h-60 rounded-lg object-contain bg-gray-100 w-full"
          />
          <button 
            @click="removeImage" 
            class="absolute top-2 right-2 bg-gray-800 bg-opacity-70 text-white rounded-full p-1 hover:bg-opacity-90 transition-opacity"
          >
            <span class="material-symbols-outlined text-sm">close</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import PeerAvatar from './PeerAvatar.vue';

export default {
  components: {
    PeerAvatar
  },
  data() {
    return {
      postContent: '',
      isExpanded: false,
      selectedImage: null,
      selectedImageFile: null,
      loading: false
    };
  },
  computed: {
    ...mapState(['user', 'profile']),
    
    currentUserAvatar() {
      return {
        name: this.profile?.name || this.user?.email || 'Student',
        profilePicture: this.profile?.profilePicture || ''
      };
    },
    
    canSubmit() {
      return this.postContent.trim().length > 0 || this.selectedImage;
    }
  },
  methods: {
    selectImage() {
      this.$refs.fileInput.click();
    },
    
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Validate file type
      if (!file.type.match('image.*')) {
        this.$store.dispatch('showAlert', { 
          message: 'Please select an image file', 
          type: 'error' 
        });
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.$store.dispatch('showAlert', { 
          message: 'Image size should be less than 5MB', 
          type: 'error' 
        });
        return;
      }
      
      this.selectedImageFile = file;
      this.selectedImage = URL.createObjectURL(file);
    },
    
    removeImage() {
      this.selectedImage = null;
      this.selectedImageFile = null;
      this.$refs.fileInput.value = '';
    },
    
    async submitPost() {
      if (!this.canSubmit || this.loading) return;
      
      this.loading = true;
      
      try {
        let imageUrl = null;
        
        // Upload image if selected
        if (this.selectedImageFile) {
          const { uploadImage } = await import('@/composables/useStorage');
          const path = `posts/${this.user.uid}/${Date.now()}_${this.selectedImageFile.name}`;
          // Pass true as the third parameter to enable compression
          const result = await uploadImage(this.selectedImageFile, path, true);
          
          if (result.success) {
            imageUrl = result.url;
          } else {
            throw new Error('Failed to upload image');
          }
        }
        
        // Create post
        const success = await this.$store.dispatch('createPost', {
          content: this.postContent.trim(),
          imageUrl
        });
        
        if (success) {
          this.$store.dispatch('showAlert', {
            message: 'Post created successfully!',
            type: 'success'
          });
          
          // Reset form
          this.postContent = '';
          this.removeImage();
          this.isExpanded = false;
        } else {
          throw new Error('Failed to create post');
        }
      } catch (error) {
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to create post',
          type: 'error'
        });
      } finally {
        this.loading = false;
      }
    },
    
    cancelPost() {
      this.postContent = '';
      this.removeImage();
      this.isExpanded = false;
    }
  }
};
</script>
